import { useEffect, useMemo, useState } from "react";
import {
	AlertTriangle,
	CheckCircle2,
	Clipboard,
	Info,
	LifeBuoy,
	Loader2,
} from "lucide-react";
import { technicalAuditService } from "@/services/technicalAuditService";
import type {
	ImageDimensionsOverride,
	PageIssue,
	ProposedFix,
	TechnicalAuditFixPreview,
} from "@/types/technicalAudit";
import {
	readHreflangPreview,
	readImageDimensionsPreview,
	readMultipleMetaPreview,
	readSitemapPreview,
	SOURCE_EDIT_ISSUE_TYPES,
} from "@/types/technicalAudit";
import {
	useSourcePublishState,
	useRecoveryActions,
	useRequestManualFix,
} from "@/hooks/useTechnicalAuditIssues";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { FixingLoader, FixingOverlay } from "@/components/ui/FixingLoader";
import { getIssueTypeHelp } from "./issueTypeHelp";
import { RecoveryActions } from "./RecoveryActions";
import { PublishFirstNotice } from "./PublishFirstNotice";
import {
	buildBeforeAfter,
	hasApplicableExpansion,
	orphanFixObject,
	stringifyValue,
} from "./issueBeforeAfter";
import { PlainBeforeAfter } from "./PlainBeforeAfter";

interface ApplyFixPreviewDialogProps {
	issue: PageIssue | null;
	/** Specialist-edited fix value (e.g. an edited destination URL) so the
	 *  preview "After" matches what will actually be applied. */
	editedValue?: ProposedFix;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Confirm apply. `dimensions` is set ONLY for image_missing_dimensions when
	 *  the specialist actually edited the measured width/height — it flows as
	 *  top-level `{width, height}` in the apply POST body. Unedited = undefined,
	 *  so the apply behaves exactly as before. */
	onConfirm: (issueId: string, dimensions?: ImageDimensionsOverride) => void;
	isApplying?: boolean;
	/** missing_from_sitemap: whether the user already ticked the deliberate
	 *  confirmation in the issue drawer (MissingFromSitemapCard). The apply body's
	 *  `confirm_site_wide` is composed from this same state, so a needs_confirm
	 *  row cannot be confirmed here without it — this closes the "open the preview
	 *  directly and hit Confirm" path. */
	siteWideConfirmed?: boolean;
}

// Exported for tests: parse a manual dimension input — a whole number between
// 1 and 8192 (the backend's accepted range) or null when invalid.
export const parseDimensionInput = (raw: string): number | null => {
	const trimmed = raw.trim();
	if (!/^\d+$/.test(trimmed)) return null;
	const value = Number.parseInt(trimmed, 10);
	return value >= 1 && value <= 8192 ? value : null;
};

// >2% deviation from the measured W:H ratio — enough to visibly distort the
// image or shift layout, so the editor shows a (non-blocking) warning.
const RATIO_WARNING_THRESHOLD = 0.02;

// Human-readable label for which existing <meta description> tag the LLM kept.
// "meta_1"/"meta_2" map to the 1-based position shown in the current-tags list;
// "synthesized" means it wrote a fresh single description (no existing tag kept).
const recommendedSourceLabel = (source?: string): string => {
	if (source === "meta_1") return "Keep tag #1; remove the other(s).";
	if (source === "meta_2") return "Keep tag #2; remove the other(s).";
	if (source === "synthesized")
		return "New combined description — replace all existing tags with this one.";
	return "Keep this one description and remove the duplicate tag(s).";
};

// 0-based index of the current meta description the recommendation keeps, so the
// current-tags list can mark it "(keep)" vs "(remove)". Only meta_1/meta_2 map
// to an existing tag; "synthesized" keeps none of them verbatim.
const keptMetaIndexFromSource = (source?: string): number | null => {
	if (source === "meta_1") return 0;
	if (source === "meta_2") return 1;
	return null;
};

const getHost = (url?: string | null): string => {
	if (!url) return "Not available";
	try {
		return new URL(url).host;
	} catch {
		return url;
	}
};

// Exported for tests: the "Target WP Field" label per issue type. `preview` is
// optional — it only refines the label for shapes that depend on the fetched
// plan (a CLUSTER hreflang fix writes tags on TWO pages, not one).
export const getTargetField = (
	issue: PageIssue | null,
	preview?: TechnicalAuditFixPreview | null,
): string => {
	if (!issue) return "Not available";
	switch (issue.issue_type) {
		case "alt_text":
			return "WordPress media alt text";
		case "title_meta":
			return "Page SEO title / meta description";
		case "title_too_short":
		case "duplicate_title":
		case "page_title_same_as_h1":
		case "page_title_missing":
		case "page_title_over_60_chars":
		case "page_title_below_30_chars":
		case "serp_title_truncated":
			return "Page SEO title (Yoast/RankMath meta)";
		case "duplicate_meta":
		case "meta_missing":
		case "meta_description_over_155_chars":
		case "meta_description_below_70_chars":
		case "serp_meta_truncated":
			return "Page meta description (Yoast/RankMath meta)";
		case "h1":
		case "h1_missing":
		case "h1_multiple":
		case "h1_too_long":
			return "Rendered page H1";
		case "redirect":
			return "Redirect or linked target URL";
		case "broken_link":
		case "broken_internal":
		case "broken_external":
		case "links_to_redirect":
			return "Page content link reference";
		case "broken_image":
			return "Page content image reference";
		case "image_missing_dimensions":
			return "Page content image tag (width/height attributes)";
		case "large_image":
			return "WordPress media file (recompressed in place)";
		case "orphan_page":
		case "low_internal_links":
			return "Source page body (new internal link)";
		case "low_readability":
			return "Page body content";
		case "thin_content":
			return "Post body (reviewed sections appended)";
		case "canonical":
			return "Page canonical URL (Yoast/RankMath meta)";
		case "noindex_important":
			return "Page robots meta (Yoast/RankMath — index/noindex)";
		case "hreflang_error": {
			const plan = readHreflangPreview(preview?.planned_payload);
			return plan?.cluster && (plan.pages?.length ?? 0) >= 2
				? "Hreflang link tags on 2 pages (rendered head)"
				: "Page hreflang link tags (rendered head)";
		}
		default:
			return "Recommendation record";
	}
};

export function ApplyFixPreviewDialog({
	issue,
	editedValue,
	open,
	onOpenChange,
	onConfirm,
	isApplying = false,
	siteWideConfirmed = false,
}: ApplyFixPreviewDialogProps) {
	const [preview, setPreview] = useState<TechnicalAuditFixPreview | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);

	useEffect(() => {
		if (!open || !issue) return;

		let ignore = false;
		Promise.resolve().then(() => {
			if (ignore) return;
			setIsLoading(true);
			setError(null);
			setPreview(null);

			technicalAuditService
				.previewFix(issue.id)
				.then((data) => {
					if (!ignore) setPreview(data);
				})
				.catch((err: unknown) => {
					const detail =
						(err as { response?: { data?: { detail?: string } }; message?: string })
							.response?.data?.detail ||
						(err as { message?: string }).message ||
						"Failed to load fix preview";
					if (!ignore) setError(detail);
				})
				.finally(() => {
					if (!ignore) setIsLoading(false);
				});
		});

		return () => {
			ignore = true;
		};
	}, [open, issue]);

	// Pre-apply publish-first check: a source-page link fix on a page that 404s to
	// the public is moot until republished — disable Apply and prompt to publish.
	// Gated to eligible types + non-terminal statuses so nothing else fetches.
	const sourcePublishEligible = Boolean(
		open &&
			issue &&
			SOURCE_EDIT_ISSUE_TYPES.has(issue.issue_type) &&
			(issue.fix_status === "pending" ||
				issue.fix_status === "approved" ||
				issue.fix_status === "failed"),
	);
	const { data: sourcePublishState } = useSourcePublishState(
		issue?.id,
		sourcePublishEligible,
	);
	const sourceNotPublic = Boolean(sourcePublishState?.source_not_public);

	// A failed fix with no self-serve recovery kind can't be retried — replace the
	// Apply button with "Request expert fix" so the user doesn't re-run a doomed apply.
	const isFailedIssue = issue?.fix_status === "failed";
	const { data: dialogRecovery } = useRecoveryActions(issue?.id, isFailedIssue);
	const requestManualFix = useRequestManualFix();
	const isManualFailure = isFailedIssue && !dialogRecovery?.kind;

	const help = issue ? getIssueTypeHelp(issue.issue_type) : null;
	const diff = useMemo(
		() => buildBeforeAfter(issue, preview, editedValue),
		[issue, preview, editedValue],
	);
	const isAutoFixable = preview?.is_auto_fixable ?? help?.fixMode === "auto";
	// The staged action can change what actually runs (e.g. an AI section rewrite
	// on a broken_external issue) — reflect that in the Fix / Execution Mode label
	// instead of the issue-type default ("DETERMINISTIC").
	const stagedAction = ((): string | undefined => {
		const v: unknown =
			editedValue ?? issue?.approved_value ?? issue?.proposed_value;
		if (!v) return undefined;
		if (typeof v === "object")
			return (v as Record<string, unknown>).action as string | undefined;
		if (typeof v === "string") {
			try {
				return (JSON.parse(v) as Record<string, unknown>).action as
					| string
					| undefined;
			} catch {
				return undefined;
			}
		}
		return undefined;
	})();
	const fixModeLabel =
		stagedAction === "rewrite_section"
			? "AI REWRITE"
			: help
				? help.fixMode.toUpperCase()
				: "MANUAL";
	// Phase 1 thin_content (blog-only additive apply): append-only WordPress write,
	// so the preview uses additive framing — never the destructive "replace" copy.
	const isThinContent = issue?.issue_type === "thin_content";
	// multiple_meta is now apply-able: the single recommended description is
	// written to BOTH the post excerpt AND the SEO-plugin meta, so every emitted
	// <meta description> carries the same value (two identical tags may still
	// remain until a theme-level cleanup). It surfaces the LLM single-meta
	// recommendation here.
	const isMultipleMeta = issue?.issue_type === "multiple_meta";
	const multipleMeta = useMemo(
		() =>
			isMultipleMeta
				? readMultipleMetaPreview(preview?.planned_payload)
				: null,
		[isMultipleMeta, preview],
	);
	// CLUSTER hreflang fix: Apply writes tags to TWO pages atomically ([issue
	// page A, partner page B]) — the confirm copy must never read single-page.
	const hreflangClusterPages = useMemo(() => {
		if (issue?.issue_type !== "hreflang_error") return null;
		const plan = readHreflangPreview(preview?.planned_payload);
		return plan?.cluster && (plan.pages?.length ?? 0) >= 2
			? (plan.pages ?? null)
			: null;
	}, [issue, preview]);
	// missing_from_sitemap: the fix is cause-dependent, so the confirm step must
	// respect the diagnosed decision. refuse / not_an_issue can never be applied
	// from here, and a needs_confirm cause (hand-excluded page, or a whole post
	// type excluded site-wide) can only be applied when the user already ticked
	// the deliberate confirmation in the drawer — which is also the sole source of
	// the apply body's confirm_site_wide.
	// (Plain derivation, not useMemo: readSitemapPreview is a cheap pure narrow of
	// an already-fetched payload.)
	const sitemapPlan =
		issue?.issue_type === "missing_from_sitemap"
			? readSitemapPreview(preview?.planned_payload)
			: null;
	const sitemapNeedsConfirm = sitemapPlan?.decision === "needs_confirm";
	const sitemapBlocked = Boolean(
		sitemapPlan &&
			(sitemapPlan.decision === "refuse" ||
				sitemapPlan.decision === "not_an_issue" ||
				(sitemapNeedsConfirm && !siteWideConfirmed)),
	);
	const currentMetaDescriptions =
		multipleMeta?.current_meta_descriptions ?? [];
	const keptMetaIndex = keptMetaIndexFromSource(
		multipleMeta?.recommended_source,
	);
	// image_missing_dimensions: when the dry-run resolved an applyable content
	// edit (apply_preview_status "ready"), the specialist can override the
	// measured width/height here before Apply writes them to WordPress. The
	// preview endpoint doesn't accept overrides, so the edited After tag below is
	// an optimistic client-side substitution.
	const isImageDims = issue?.issue_type === "image_missing_dimensions";
	const imageDims = useMemo(
		() =>
			isImageDims
				? readImageDimensionsPreview(preview?.planned_payload)
				: null,
		[isImageDims, preview],
	);
	const imageDimsReady = Boolean(imageDims?.ready);
	const measuredWidth = imageDims?.width;
	const measuredHeight = imageDims?.height;
	const [widthInput, setWidthInput] = useState("");
	const [heightInput, setHeightInput] = useState("");
	useEffect(() => {
		// Prefill from the measured values whenever a (new) preview resolves —
		// also clears any stale edits when the dialog moves to another issue.
		// eslint-disable-next-line react-hooks/set-state-in-effect -- sync local draft with the fetched preview
		setWidthInput(imageDimsReady ? String(measuredWidth) : "");
		// eslint-disable-next-line react-hooks/set-state-in-effect -- sync local draft with the fetched preview
		setHeightInput(imageDimsReady ? String(measuredHeight) : "");
	}, [imageDimsReady, measuredWidth, measuredHeight]);
	const widthValue = parseDimensionInput(widthInput);
	const heightValue = parseDimensionInput(heightInput);
	const dimensionsEdited = Boolean(
		imageDimsReady &&
			(widthInput.trim() !== String(measuredWidth) ||
				heightInput.trim() !== String(measuredHeight)),
	);
	const dimensionsInvalid =
		dimensionsEdited && (widthValue === null || heightValue === null);
	// Sent to the apply ONLY when the user actually changed a value (and both
	// fields are valid); otherwise undefined and the request body is unchanged.
	const dimensionOverride: ImageDimensionsOverride | undefined =
		dimensionsEdited && widthValue !== null && heightValue !== null
			? { width: widthValue, height: heightValue }
			: undefined;
	// Non-blocking aspect-ratio guard: warn when the edited W:H ratio deviates
	// >2% from the measured ratio ("may distort or shift layout").
	const ratioDeviates = Boolean(
		dimensionOverride &&
			measuredWidth &&
			measuredHeight &&
			Math.abs(
				dimensionOverride.width /
					dimensionOverride.height /
					(measuredWidth / measuredHeight) -
					1,
			) > RATIO_WARNING_THRESHOLD,
	);
	// Live-updating After tag: substitute the edited values into the planned
	// after tag so the preview always shows exactly what will be written.
	const liveAfterTag = useMemo(() => {
		if (!imageDimsReady) return null;
		const base = imageDims?.after_tag ?? diff.after;
		return base
			.replace(/width="[^"]*"/, `width="${widthInput.trim() || "…"}"`)
			.replace(/height="[^"]*"/, `height="${heightInput.trim() || "…"}"`);
	}, [imageDimsReady, imageDims?.after_tag, diff.after, widthInput, heightInput]);
	// Mirror the card's fetch-failure caution in the preview when the generated
	// packet isn't grounded in the live post body (blog apply path).
	const thinContentPacket =
		orphanFixObject(editedValue) ?? orphanFixObject(issue?.approved_value);
	const thinContentNotGrounded =
		isThinContent && thinContentPacket?.grounded === false;
	// Backend appends only sections with a non-empty draft (+ non-empty draft
	// additions); if neither exists the append is a no-op and the backend bails,
	// so the apply must be disabled with a "nothing to apply" note.
	const thinContentNothingToApply =
		isThinContent && !hasApplicableExpansion(thinContentPacket);
	const title = isThinContent ? "Preview expansion" : help?.title || "Apply Fix";
	// Block the additive apply when the brief isn't grounded (don't append to a
	// post body we couldn't read) or when there's nothing applicable to append.
	const canConfirm =
		Boolean(issue) &&
		!isLoading &&
		!isApplying &&
		!error &&
		!thinContentNotGrounded &&
		!thinContentNothingToApply &&
		!dimensionsInvalid &&
		!sitemapBlocked &&
		!sourceNotPublic;
	const recommendation = stringifyValue(
		// For multiple_meta the useful thing to copy is the recommended single
		// description (the actual value to paste into the SEO plugin), not the
		// generic "remove the duplicate tag" sentence.
		(isMultipleMeta && multipleMeta?.recommended_meta_description) ||
			preview?.planned_payload?.recommendation ||
			preview?.planned_payload?.approved_value ||
			preview?.planned_payload?.expected_output ||
			diff.after,
	);
		const executionMode = isThinContent
			? "Append to WordPress post (additive)"
			: isAutoFixable
				? "Apply to WordPress"
				: preview?.planned_payload?.preview_state === "protected"
					? "Protected, no WordPress change"
					: "Recommendation only, no WordPress change";
	// While the preview is loading the confirm is disabled — say so on the
	// button itself, otherwise the first click lands on a silently dead button
	// (slow previews, e.g. image-dimension resolution, made this a real miss).
	const confirmLabel = isLoading
		? "Loading preview…"
		: isApplying
			? isThinContent
				? "Applying expansion…"
				: isAutoFixable
					? "Applying..."
					: "Confirming..."
			: isThinContent
				? "Apply reviewed expansion"
				: isAutoFixable
					// After a failure this is a SECOND attempt — say so, so the
					// operator knows the click re-runs the apply with whatever
					// they changed rather than confirming the run that just failed.
					? (isFailedIssue ? "Retry apply" : "Confirm Apply")
					: "Confirm recommendation";

	const handleCopyRecommendation = async () => {
		await navigator.clipboard?.writeText(recommendation);
		setCopied(true);
		window.setTimeout(() => setCopied(false), 1600);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			{/* Grow HORIZONTALLY, not vertically. Cause-rich previews (a sitemap
			    exclusion with blame + an override consent block, a hreflang cluster)
			    run long, and this dialog used to be an unbounded `max-w-2xl` column:
			    the content simply pushed DialogFooter — and with it "Apply to
			    WordPress" — off the bottom of the screen, unreachable. Wider means
			    fewer wrapped lines; the bounded height + scrollable body + shrink-0
			    footer is what actually GUARANTEES Apply stays on screen, at any
			    content length and any viewport. Same structure IssueDiffDrawer uses. */}
			<DialogContent className="flex max-h-[88vh] w-[92vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[min(1100px,calc(100vw-2rem))]">
				{/* DialogContent is position:fixed (centered) — do NOT add `relative`
				    here or it drops the modal into page flow (off-screen). The
				    absolute overlay below still anchors to this fixed container. */}
				<FixingOverlay show={isApplying} label="Applying fix…" />
				<DialogHeader className="shrink-0 border-b border-border px-6 pb-4 pt-6">
					<DialogTitle>{isThinContent ? title : `Review Fix: ${title}`}</DialogTitle>
					<DialogDescription>
						{isThinContent
							? "Append-only: your existing post content stays exactly as-is. The reviewed sections below are added to the end of the post."
							: "Check whether this action will change WordPress or only save a recommendation."}
					</DialogDescription>
				</DialogHeader>

				<div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
					{/* If the previous apply on this issue FAILED, surface the same
					    one-click recovery actions here — this dialog is where the user
					    is at the moment of a retry, so they shouldn't have to hunt for
					    them on the row/other drawer. */}
					{issue?.fix_status === "failed" && (
						<RecoveryActions
							issueId={issue.id}
							isFailed
							onNavigate={() => onOpenChange(false)}
						/>
					)}

					{/* Source page isn't publicly live — no fix offered; publish first. */}
					{sourceNotPublic && issue && (
						<PublishFirstNotice
							issueId={issue.id}
							message={sourcePublishState?.message}
							onNavigate={() => onOpenChange(false)}
						/>
					)}

					{isLoading && (
						<div className="rounded-md border bg-muted p-4 text-sm text-muted-foreground">
							Loading preview...
						</div>
					)}

					{error && (
						<div className="flex gap-2 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-4 text-sm text-red-700 dark:text-red-400">
							<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
							<span>{error}</span>
						</div>
					)}

					{!isLoading && !error && (
						<>
							{thinContentNotGrounded && (
								<div
									data-testid="thin-content-preview-fetch-failure"
									className="flex items-start gap-2 rounded-lg border border-coral/30 bg-coral-light p-4 text-sm text-coral-dark"
								>
									<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
									<span>
										Couldn't read the current post; this brief isn't grounded in live
										content.
									</span>
								</div>
							)}
							{thinContentNothingToApply && !thinContentNotGrounded && (
								<div
									data-testid="thin-content-preview-nothing-to-apply"
									className="flex items-start gap-2 rounded-lg border border-coral/30 bg-coral-light p-4 text-sm text-coral-dark"
								>
									<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
									<span>
										Nothing to apply yet — add draft copy to at least one section.
									</span>
								</div>
							)}
							{isThinContent ? (
								// thin_content is_auto_fixable is False by design (apply runs via
								// the conditional append path, not the generic auto-fix flag), so
								// drive this box off isThinContent — NOT isAutoFixable — to avoid
								// a "won't change WordPress" message above an append-to-WP button.
								<div className="flex gap-2 rounded-md border border-teal/30 bg-teal-light/40 p-4 text-sm text-foreground">
									<CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-teal-dark" />
									<div>
										<div className="font-medium text-teal-dark">
											WordPress update (append-only)
										</div>
										<div className="mt-1 text-foreground/80">
											This appends the reviewed sections to the end of the post in
											WordPress. Existing content is preserved.
										</div>
									</div>
								</div>
							) : (
								<div className="flex gap-2 rounded-md border bg-muted p-4 text-sm text-foreground">
									{isAutoFixable ? (
										<CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
									) : (
										<Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-600 dark:text-sky-400" />
									)}
									<div>
										<div className="font-medium text-foreground">
											{isAutoFixable ? "WordPress update" : "Recommendation only"}
										</div>
										<div className="mt-1">
											{isAutoFixable
												? isMultipleMeta
													? "Updates the post excerpt + SEO meta to the single recommended description. Note: two identical tags may remain until a theme-level cleanup."
													: hreflangClusterPages
														? "This fix updates 2 pages in one step: the page you selected AND its partner page. Hreflang return links must match on both sides, so both tag sets are written together."
														: "This issue type is allowed to run a controlled WordPress update."
												: isMultipleMeta
													? "This page has more than one meta description tag, which can't be safely removed automatically across every page builder. Use the recommended single description below, then keep it and delete the duplicate tag(s) in WordPress."
													: preview?.manual_reason ||
														// e.g. a non_reciprocal hreflang error: auto-fixable in
														// general but excluded for this row (cluster-wide return
														// links land in Phase 2).
														preview?.auto_fix_excluded_reason ||
														"This saves the recommendation in Technical Audit. It will not change WordPress."}
										</div>
									</div>
								</div>
							)}

							<div className="grid gap-3 rounded-md border bg-card p-3 text-sm sm:grid-cols-2">
								<div>
									<div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
										Target Site
									</div>
									<div className="mt-1 break-words text-foreground">
										{getHost(issue?.page_url)}
									</div>
								</div>
								<div>
									<div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
										{hreflangClusterPages
											? "Target Pages (2 — applied together)"
											: "Target Page / Asset"}
									</div>
									{hreflangClusterPages ? (
										<div
											data-testid="apply-preview-cluster-pages"
											className="mt-1 space-y-1 break-words text-foreground"
										>
											{hreflangClusterPages.map((page, idx) => (
												<div key={`${page.url}-${idx}`} className="break-all">
													{page.url}
													{idx === 0 ? "" : " (partner page)"}
												</div>
											))}
										</div>
									) : (
										<div className="mt-1 break-words text-foreground">
											{issue?.page_url || "Not available"}
										</div>
									)}
								</div>
								<div>
									<div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
										Target WP Field
									</div>
									<div className="mt-1 text-foreground">
										{getTargetField(issue, preview)}
									</div>
								</div>
								<div>
									<div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
										Fix / Execution Mode
									</div>
									<div className="mt-1 text-foreground">
										{fixModeLabel} / {executionMode}
									</div>
								</div>
							</div>

							{isMultipleMeta ? (
								// Recommendation-only: surface the LLM single-meta suggestion so
								// it can be tested. Highlight the recommended description (teal),
								// say which existing tag to keep vs remove, give the reason, and
								// list the current descriptions for context.
								<div
									data-testid="multiple-meta-preview"
									className="space-y-3"
								>
									{multipleMeta?.recommended_meta_description ? (
										<div className="rounded-md border border-teal/40 bg-teal-light p-4">
											<div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-teal-dark">
												<CheckCircle2 className="h-4 w-4" />
												Recommended single meta description
											</div>
											<p
												data-testid="multiple-meta-recommended"
												className="mt-2 whitespace-pre-wrap break-words text-sm text-foreground"
											>
												{multipleMeta.recommended_meta_description}
											</p>
											<div className="mt-2 text-xs text-teal-dark">
												{recommendedSourceLabel(
													multipleMeta.recommended_source,
												)}
											</div>
											{multipleMeta.recommendation_reason && (
												<p className="mt-2 text-xs text-foreground/70">
													<span className="font-medium">Why:</span>{" "}
													{multipleMeta.recommendation_reason}
												</p>
											)}
										</div>
									) : (
										<div className="flex items-start gap-2 rounded-md border border-coral/30 bg-coral-light p-4 text-sm text-coral-dark">
											<Info className="mt-0.5 h-4 w-4 shrink-0" />
											<span>
												{multipleMeta?.recommendation ||
													"No single-meta recommendation was generated. Keep one intended meta description and remove the duplicate tag in WordPress."}
											</span>
										</div>
									)}

									{currentMetaDescriptions.length > 0 && (
										<div className="rounded-md border border-border bg-warm-bg p-3">
											<div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
												Current meta descriptions on the page (
												{multipleMeta?.meta_description_count ??
													currentMetaDescriptions.length}{" "}
												found)
											</div>
											<ul className="space-y-2">
												{currentMetaDescriptions.map((desc, idx) => {
													const isKept = keptMetaIndex === idx;
													return (
														<li
															key={`${idx}-${desc.slice(0, 24)}`}
															className={`rounded border p-2 text-xs ${
																isKept
																	? "border-teal/40 bg-teal-light/40 text-foreground"
																	: "border-border bg-card text-foreground/70"
															}`}
														>
															<span
																className={`mr-2 font-semibold ${
																	isKept
																		? "text-teal-dark"
																		: "text-muted-foreground"
																}`}
															>
																#{idx + 1}{" "}
																{isKept ? "(keep)" : "(remove)"}
															</span>
															<span className="whitespace-pre-wrap break-words">
																{desc}
															</span>
														</li>
													);
												})}
											</ul>
										</div>
									)}
								</div>
							) : isThinContent ? (
								// Append-only framing: existing content is preserved (neutral
								// tone, not destructive red); the appended region is marked teal.
								<div className="grid gap-3 sm:grid-cols-2">
									<div className="rounded-md border border-border bg-warm-bg p-3">
										<div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
											Current post (unchanged)
										</div>
										<div className="max-h-56 overflow-auto whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground/80">
											{diff.before}
										</div>
									</div>
									<div className="rounded-md border border-teal/30 bg-card p-3">
										<div className="mb-2 text-xs font-semibold uppercase tracking-wide text-teal-dark">
											After apply — existing content + appended sections
										</div>
										<div
											data-testid="thin-content-preview-appended"
											className="border-l-2 border-teal bg-teal-light/30 pl-3"
										>
											<div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-teal-dark">
												Appended sections (new)
											</div>
											<div className="max-h-56 overflow-auto whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground/80">
												{diff.after}
											</div>
										</div>
									</div>
								</div>
							) : (
								<PlainBeforeAfter
									before={diff.before}
									after={liveAfterTag ?? diff.after}
									afterTestId={imageDimsReady ? "image-dims-after-tag" : undefined}
								/>
							)}

							{imageDimsReady && (
								<div
									data-testid="image-dims-editor"
									className="space-y-2 rounded-md border border-teal/30 bg-teal-light/20 p-3"
								>
									<div className="text-xs font-semibold uppercase tracking-wide text-teal-dark">
										Dimensions to write
									</div>
									<div className="flex flex-wrap items-end gap-3">
										<label className="flex flex-col gap-1 text-xs font-medium text-foreground/80">
											Width px
											<Input
												type="number"
												inputMode="numeric"
												min={1}
												max={8192}
												value={widthInput}
												onChange={(e) => setWidthInput(e.target.value)}
												aria-label="Width px"
												data-testid="image-dims-width-input"
												className="h-8 w-24"
											/>
										</label>
										<label className="flex flex-col gap-1 text-xs font-medium text-foreground/80">
											Height px
											<Input
												type="number"
												inputMode="numeric"
												min={1}
												max={8192}
												value={heightInput}
												onChange={(e) => setHeightInput(e.target.value)}
												aria-label="Height px"
												data-testid="image-dims-height-input"
												className="h-8 w-24"
											/>
										</label>
										<div className="pb-1.5 text-xs text-muted-foreground">
											measured: {measuredWidth}×{measuredHeight}
										</div>
										{dimensionsEdited && (
											<Button
												type="button"
												variant="ghost"
												size="sm"
												onClick={() => {
													setWidthInput(String(measuredWidth));
													setHeightInput(String(measuredHeight));
												}}
												data-testid="image-dims-reset"
												className="h-8 px-2 text-xs text-teal-dark hover:bg-teal-light"
											>
												Reset to measured
											</Button>
										)}
									</div>
									{dimensionsInvalid && (
										<p
											data-testid="image-dims-error"
											className="text-xs text-destructive"
										>
											Width and height must be whole numbers between 1 and
											8192.
										</p>
									)}
									{!dimensionsInvalid && ratioDeviates && (
										<p
											data-testid="image-dims-ratio-warning"
											className="flex items-center gap-1.5 text-xs text-coral-dark"
										>
											<AlertTriangle className="h-3.5 w-3.5 shrink-0" />
											These values change the image&apos;s measured aspect
											ratio — the image may distort or shift layout.
										</p>
									)}
								</div>
							)}

							{sitemapPlan && sitemapBlocked && (
							<div
								data-testid="sitemap-apply-blocked"
								className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
							>
								<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
								<span>
									{sitemapNeedsConfirm
										? "This change needs your explicit confirmation first. Close this preview and tick the confirmation on the issue before applying — it isn't a routine one-page fix."
										: sitemapPlan.decision === "not_an_issue"
											? sitemapPlan.blocking_cause.code === "already_in_sitemap"
												? "Nothing to apply: we re-checked your live sitemap and this page IS listed in it. The audit row is out of date — skip it."
												: "Nothing to apply: this page isn't actually missing from your sitemap (SEO plugins list the homepage separately). Skip the issue instead."
											: sitemapPlan.refusal_reason ||
												"We won't apply this automatically — the exclusion isn't ours to change safely. Fix it at the source, or skip the issue."}
								</span>
							</div>
						)}

						{preview?.planned_payload?.success_criteria && (
								<div className="rounded-md border bg-card p-3 text-sm">
									<div className="mb-1 font-medium text-foreground">Success criteria</div>
									<div className="text-muted-foreground">
										{stringifyValue(preview.planned_payload.success_criteria)}
									</div>
								</div>
							)}

							{!isAutoFixable && (
								<Button
									type="button"
									variant="outline"
									onClick={handleCopyRecommendation}
									className="gap-2"
								>
									<Clipboard className="h-4 w-4" />
									{copied ? "Copied" : "Copy recommendation"}
								</Button>
							)}
						</>
					)}
				</div>

				{/* shrink-0: never let a long body compress the footer out of view —
				    this is the line that keeps Apply reachable. */}
				<DialogFooter className="shrink-0 border-t border-border px-6 py-4">
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={isApplying}
					>
						Cancel
					</Button>
					{isManualFailure && (
						<Button
							variant={isAutoFixable ? "outline" : "default"}
							onClick={() =>
								issue &&
								requestManualFix.mutate(issue.id, {
									onSuccess: () => onOpenChange(false),
								})
							}
							disabled={!issue || requestManualFix.isPending}
							className={
								isAutoFixable
									? "gap-1.5"
									: "gap-1.5 bg-coral text-white hover:bg-coral-hover"
							}
						>
							{requestManualFix.isPending ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<LifeBuoy className="h-4 w-4" />
							)}
							Request expert fix
						</Button>
					)}
					{/* An auto-fixable failure stays RETRYABLE — the expert button
					    accompanies Apply, it does not replace it.

					    This used to be a hard either/or: any failed issue with no
					    self-serve recovery kind lost its Apply button entirely. For
					    internal links that removed the only route the operator
					    actually needed — they choose the source pages and anchors, so
					    "that anchor isn't in the editable content" is fixed by picking
					    differently or regenerating, not by filing a ticket. The drawer
					    already carved out this exception; the dialog did not, so
					    "Apply selected links" opened a Review Fix with nothing but
					    Request expert fix. */}
					{(!isManualFailure || isAutoFixable) && (
						<Button
							onClick={() => issue && onConfirm(issue.id, dimensionOverride)}
							disabled={!canConfirm}
							data-testid="preview-confirm-apply"
							className="bg-teal text-white hover:bg-teal-hover"
						>
							{isApplying ? (
								<FixingLoader label={confirmLabel} />
							) : isLoading ? (
								<>
									<Loader2 className="h-4 w-4 animate-spin" />
									{confirmLabel}
								</>
							) : (
								confirmLabel
							)}
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
